import * as d3 from 'd3';
import { ProfilePoint, SectionViewConfig, SurfaceFeature, GeologyLayer, HDDPathPoint } from './types';
import * as units from './units';

export class CrossSectionView {
  private svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any>;
  private profile: ProfilePoint[];
  private config: SectionViewConfig;
  private hddPath: HDDPathPoint[] | null = null;

  constructor(
    container: string,
    profile: ProfilePoint[],
    config: Partial<SectionViewConfig> = {},
    hddPath: HDDPathPoint[] | null = null
  ) {
    this.profile = profile;
    this.hddPath = hddPath;
    this.config = {
      width: config.width || 1400,
      height: config.height || 600,
      verticalExaggeration: config.verticalExaggeration || 2
    };

    // Clear container
    d3.select(container).selectAll('*').remove();

    this.svg = d3.select(container)
      .append('svg')
      .attr('width', this.config.width)
      .attr('height', this.config.height);

    this.render();
  }
  
  private render() {
    const margin = { top: 50, right: 50, bottom: 50, left: 80 };
    const width = this.config.width - margin.left - margin.right;
    const height = this.config.height - margin.top - margin.bottom;
    
    const g = this.svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    
    // Scales
    const maxDistance = this.profile[this.profile.length - 1].distance;
    const elevations = this.profile.map(p => p.elevation);
    let minElev = Math.min(...elevations) - 10;
    let maxElev = Math.max(...elevations) + 50;

    // Include HDD path in elevation range if present
    if (this.hddPath && this.hddPath.length > 0) {
      const hddElevations = this.hddPath.map(p => p.elevation);
      const hddMin = Math.min(...hddElevations);
      const hddMax = Math.max(...hddElevations);
      minElev = Math.min(minElev, hddMin - 5);
      maxElev = Math.max(maxElev, hddMax + 10);
    }
    
    const xScale = d3.scaleLinear()
      .domain([0, maxDistance])
      .range([0, width]);
    
    const yScale = d3.scaleLinear()
      .domain([minElev, maxElev])
      .range([height, 0]);
    
    // Draw elements (geology first so it's behind ground surface)
    this.drawGeology(g, xScale, yScale, height);
    this.drawGroundSurface(g, xScale, yScale, height);
    this.drawWaterBodies(g, xScale, yScale, height);
    this.drawBuildings(g, xScale, yScale);
    this.drawWaterways(g, xScale, yScale);
    this.drawRoads(g, xScale, yScale);
    this.drawRailways(g, xScale, yScale);

    // Draw HDD borehole path if available
    if (this.hddPath && this.hddPath.length > 0) {
      this.drawHDDPath(g, xScale, yScale);
    }

    this.drawAxes(g, xScale, yScale, width, height);
    this.drawLegend(g, width);
  }
  
  private drawGroundSurface(
    g: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>,
    height: number
  ) {
    const line = d3.line<ProfilePoint>()
      .x(d => xScale(d.distance))
      .y(d => yScale(d.elevation));
    
    const area = d3.area<ProfilePoint>()
      .x(d => xScale(d.distance))
      .y0(height)
      .y1(d => yScale(d.elevation));
    
    g.append('path')
      .datum(this.profile)
      .attr('class', 'ground-surface')
      .attr('d', area)
      .attr('fill', '#8B7355')
      .attr('opacity', 0.3)
      .style('pointer-events', 'none');  // Allow clicks to pass through to geology
    
    g.append('path')
      .datum(this.profile)
      .attr('class', 'ground-line')
      .attr('d', line)
      .attr('stroke', '#654321')
      .attr('stroke-width', 2)
      .attr('fill', 'none')
      .style('pointer-events', 'none');  // Allow clicks to pass through to geology
  }

  private drawGeology(
    g: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>,
    height: number
  ) {
    // Group consecutive points with the same geology into regions
    interface GeologyRegion {
      startIdx: number;
      endIdx: number;
      superficial?: GeologyLayer;
      bedrock?: GeologyLayer;
      soil?: GeologyLayer;  // SSURGO soil data (US)
    }

    const regions: GeologyRegion[] = [];
    let currentRegion: GeologyRegion | null = null;

    this.profile.forEach((point, idx) => {
      const superficialCode = point.geology?.superficial?.code || '';
      const bedrockCode = point.geology?.bedrock?.code || '';
      const soilCode = point.geology?.soil?.code || '';
      const currentKey = `${superficialCode}|${bedrockCode}|${soilCode}`;

      const prevPoint = idx > 0 ? this.profile[idx - 1] : null;
      const prevSuperficialCode = prevPoint?.geology?.superficial?.code || '';
      const prevBedrockCode = prevPoint?.geology?.bedrock?.code || '';
      const prevSoilCode = prevPoint?.geology?.soil?.code || '';
      const prevKey = `${prevSuperficialCode}|${prevBedrockCode}|${prevSoilCode}`;

      if (currentKey !== prevKey || !currentRegion) {
        if (currentRegion) {
          regions.push(currentRegion);
        }
        currentRegion = {
          startIdx: idx,
          endIdx: idx,
          superficial: point.geology?.superficial,
          bedrock: point.geology?.bedrock,
          soil: point.geology?.soil
        };
      } else {
        currentRegion.endIdx = idx;
      }
    });

    if (currentRegion) {
      regions.push(currentRegion);
    }

    // Draw each geology region
    regions.forEach(region => {
      const startX = xScale(this.profile[region.startIdx].distance);
      const endX = xScale(this.profile[region.endIdx].distance);
      const width = Math.max(endX - startX, 2);

      // Get elevations for this region
      const regionPoints = this.profile.slice(region.startIdx, region.endIdx + 1);
      const minElev = Math.min(...regionPoints.map(p => p.elevation));

      // Draw bedrock layer (deeper, full height from bottom)
      if (region.bedrock) {
        const bedrockArea = d3.area<ProfilePoint>()
          .x(d => xScale(d.distance))
          .y0(height)  // Bottom of chart
          .y1(d => yScale(d.elevation));  // Top at ground level

        g.append('path')
          .datum(regionPoints)
          .attr('class', 'geology-bedrock')
          .attr('d', bedrockArea)
          .attr('fill', region.bedrock.color || '#CCCCCC')
          .attr('opacity', 0.6)
          .style('cursor', 'pointer')
          .on('mouseover', (event: MouseEvent) => {
            this.showGeologyTooltip(event, region.bedrock!, 'Bedrock');
          })
          .on('mousemove', (event: MouseEvent) => {
            this.showGeologyTooltip(event, region.bedrock!, 'Bedrock');
          })
          .on('mouseout', () => {
            this.hideTooltip();
          });
      }

      // Draw superficial layer (UK - on top of bedrock, typically 2-10m thick)
      if (region.superficial) {
        const superficialThickness = 5; // Estimated thickness in meters
        const superficialArea = d3.area<ProfilePoint>()
          .x(d => xScale(d.distance))
          .y0(d => yScale(d.elevation - superficialThickness))  // Bottom
          .y1(d => yScale(d.elevation));  // Top at ground level

        g.append('path')
          .datum(regionPoints)
          .attr('class', 'geology-superficial')
          .attr('d', superficialArea)
          .attr('fill', region.superficial.color || '#D4B896')
          .attr('opacity', 0.7)
          .style('cursor', 'pointer')
          .on('mouseover', (event: MouseEvent) => {
            this.showGeologyTooltip(event, region.superficial!, 'Superficial Deposits');
          })
          .on('mousemove', (event: MouseEvent) => {
            this.showGeologyTooltip(event, region.superficial!, 'Superficial Deposits');
          })
          .on('mouseout', () => {
            this.hideTooltip();
          });
      }

      // Draw soil layer (US SSURGO - on top of bedrock, similar to superficial)
      if (region.soil) {
        // Use thickness from SSURGO if available, otherwise estimate
        const soilThickness = region.soil.thickness || 3; // meters
        const soilArea = d3.area<ProfilePoint>()
          .x(d => xScale(d.distance))
          .y0(d => yScale(d.elevation - soilThickness))  // Bottom
          .y1(d => yScale(d.elevation));  // Top at ground level

        g.append('path')
          .datum(regionPoints)
          .attr('class', 'geology-soil')
          .attr('d', soilArea)
          .attr('fill', region.soil.color || '#8B7355')
          .attr('opacity', 0.7)
          .style('cursor', 'pointer')
          .on('mouseover', (event: MouseEvent) => {
            this.showGeologyTooltip(event, region.soil!, 'Soil');
          })
          .on('mousemove', (event: MouseEvent) => {
            this.showGeologyTooltip(event, region.soil!, 'Soil');
          })
          .on('mouseout', () => {
            this.hideTooltip();
          });
      }
    });
  }

  private showGeologyTooltip(event: MouseEvent, geology: GeologyLayer, layerType: string) {
    const tooltip = d3.select('#tooltip');

    // Build tooltip content based on data source
    let content = `<strong>${layerType}: ${geology.description}</strong><br>`;

    if (geology.type === 'soil') {
      // SSURGO soil-specific display
      content += `Texture: ${geology.rockType}<br>`;
      if (geology.drainageClass) {
        content += `Drainage: ${geology.drainageClass}<br>`;
      }
      if (geology.thickness) {
        content += `Thickness: ${geology.thickness.toFixed(1)}m<br>`;
      }
    } else {
      // Rock type display (BGS, Macrostrat)
      content += `Rock Type: ${geology.rockType}<br>`;
      if (geology.age) {
        content += `Age: ${geology.age}<br>`;
      }
    }

    if (geology.source) {
      const sourceLabels: Record<string, string> = {
        'bgs': 'BGS',
        'macrostrat': 'Macrostrat',
        'ssurgo': 'USDA SSURGO',
        'usgs': 'USGS'
      };
      content += `<span style="font-size:10px;color:#999">Source: ${sourceLabels[geology.source] || geology.source}</span>`;
    }

    tooltip.html(content)
      .style('display', 'block');

    this.positionTooltip(tooltip, event);
  }

  private drawWaterBodies(
    g: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>,
    height: number
  ) {
    // Find consecutive points that are over water and group them into regions
    interface WaterRegion {
      startIdx: number;
      endIdx: number;
      name?: string;
      points: { distance: number; surfaceElevation: number; seabedElevation: number; depth: number }[];
    }

    const waterRegions: WaterRegion[] = [];
    let currentRegion: WaterRegion | null = null;

    this.profile.forEach((point, idx) => {
      const waterFeature = point.surfaceFeatures.find(f => f.type === 'water');

      if (waterFeature && point.waterSurfaceElevation !== undefined) {
        const depth = waterFeature.depth || 10;
        const waterPoint = {
          distance: point.distance,
          // Water surface is stored separately, elevation is now seabed
          surfaceElevation: point.waterSurfaceElevation,
          seabedElevation: point.elevation,  // elevation is already the seabed
          depth: depth
        };

        if (!currentRegion) {
          currentRegion = {
            startIdx: idx,
            endIdx: idx,
            name: waterFeature.name,
            points: [waterPoint]
          };
        } else {
          currentRegion.endIdx = idx;
          currentRegion.points.push(waterPoint);
        }
      } else {
        if (currentRegion) {
          waterRegions.push(currentRegion);
          currentRegion = null;
        }
      }
    });

    // Don't forget the last region if it extends to the end
    if (currentRegion) {
      waterRegions.push(currentRegion);
    }

    // Draw each water region as an area following the seabed profile
    waterRegions.forEach(region => {
      // Add transition points at shore to close gaps between land and water
      const extendedPoints = [...region.points];

      // Add entry transition point (where land meets water)
      // Use the land point's position so water connects directly to land
      if (region.startIdx > 0) {
        const landPoint = this.profile[region.startIdx - 1];
        // Shore point at land position - water starts here with no depth
        extendedPoints.unshift({
          distance: landPoint.distance,
          surfaceElevation: landPoint.elevation,
          seabedElevation: landPoint.elevation,  // At shore, seabed meets land surface
          depth: 0
        });
      }

      // Add exit transition point (where water meets land)
      if (region.endIdx < this.profile.length - 1) {
        const landPoint = this.profile[region.endIdx + 1];
        // Shore point at land position - water ends here with no depth
        extendedPoints.push({
          distance: landPoint.distance,
          surfaceElevation: landPoint.elevation,
          seabedElevation: landPoint.elevation,  // At shore, seabed meets land surface
          depth: 0
        });
      }

      if (extendedPoints.length < 2) {
        // Single point - draw a small rectangle
        const pt = extendedPoints[0];
        const x = xScale(pt.distance);
        const surfaceY = yScale(pt.surfaceElevation);
        const seabedY = yScale(pt.seabedElevation);

        g.append('rect')
          .attr('x', x - 5)
          .attr('y', surfaceY)
          .attr('width', 10)
          .attr('height', seabedY - surfaceY)
          .attr('fill', '#1E90FF')
          .attr('opacity', 0.4);
        return;
      }

      // Create area generator for water body
      const waterArea = d3.area<{ distance: number; surfaceElevation: number; seabedElevation: number }>()
        .x(d => xScale(d.distance))
        .y0(d => yScale(d.surfaceElevation))  // Water surface (top)
        .y1(d => yScale(d.seabedElevation))   // Seabed (bottom)
        .curve(d3.curveLinear);

      // Draw the water body area
      g.append('path')
        .datum(extendedPoints)
        .attr('class', 'water-body')
        .attr('d', waterArea)
        .attr('fill', '#1E90FF')
        .attr('opacity', 0.4)
        .on('mouseover', (event: MouseEvent) => {
          const avgDepth = region.points.reduce((sum, p) => sum + p.depth, 0) / region.points.length;
          const maxDepth = Math.max(...region.points.map(p => p.depth));
          this.showWaterBodyTooltip(event, region.name, avgDepth, maxDepth);
        })
        .on('mouseout', () => {
          this.hideTooltip();
        });

      // Draw water surface line
      const surfaceLine = d3.line<{ distance: number; surfaceElevation: number }>()
        .x(d => xScale(d.distance))
        .y(d => yScale(d.surfaceElevation));

      g.append('path')
        .datum(extendedPoints)
        .attr('class', 'water-surface')
        .attr('d', surfaceLine)
        .attr('stroke', '#0066CC')
        .attr('stroke-width', 2)
        .attr('fill', 'none');

      // Draw seabed line
      const seabedLine = d3.line<{ distance: number; seabedElevation: number }>()
        .x(d => xScale(d.distance))
        .y(d => yScale(d.seabedElevation));

      g.append('path')
        .datum(extendedPoints)
        .attr('class', 'seabed')
        .attr('d', seabedLine)
        .attr('stroke', '#8B4513')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '4,2')
        .attr('fill', 'none');

      // Label if named
      if (region.name) {
        const midPoint = region.points[Math.floor(region.points.length / 2)];
        g.append('text')
          .attr('x', xScale(midPoint.distance))
          .attr('y', yScale(midPoint.surfaceElevation) + 15)
          .attr('text-anchor', 'middle')
          .attr('font-size', '11px')
          .attr('fill', '#003366')
          .attr('font-weight', 'bold')
          .text(region.name);
      }
    });
  }

  private showWaterBodyTooltip(event: MouseEvent, name: string | undefined, avgDepth: number, maxDepth?: number) {
    const tooltip = d3.select('#tooltip');

    let html = `<strong>${name || 'Water Body'}</strong><br>`;
    if (maxDepth !== undefined && maxDepth !== avgDepth) {
      html += `Avg Depth: ${avgDepth.toFixed(1)}m<br>`;
      html += `Max Depth: ${maxDepth.toFixed(1)}m`;
    } else {
      html += `Depth: ${avgDepth.toFixed(1)}m`;
    }

    tooltip.html(html)
      .style('display', 'block');

    this.positionTooltip(tooltip, event);
  }

  private drawBuildings(
    g: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>
  ) {
    // Group consecutive points with buildings into building regions
    interface BuildingRegion {
      startDistance: number;
      endDistance: number;
      maxHeight: number;
      maxFoundationDepth: number;
      minElevation: number;  // Lowest ground point - building base extends here
      maxElevation: number;  // Highest ground point - building height measured from here
      name?: string;
      mass?: number;
    }

    const buildingRegions: BuildingRegion[] = [];
    let currentRegion: BuildingRegion | null = null;

    this.profile.forEach((point) => {
      const buildings = point.surfaceFeatures.filter(f => f.type === 'building');

      if (buildings.length > 0) {
        // Use the tallest building at this point
        const tallestBuilding = buildings.reduce((max, b) =>
          (b.height || 0) > (max.height || 0) ? b : max, buildings[0]);

        const height = tallestBuilding.height || 10;
        const foundationDepth = tallestBuilding.foundationDepth || 2;

        if (!currentRegion) {
          currentRegion = {
            startDistance: point.distance,
            endDistance: point.distance,
            maxHeight: height,
            maxFoundationDepth: foundationDepth,
            minElevation: point.elevation,
            maxElevation: point.elevation,
            name: tallestBuilding.name,
            mass: tallestBuilding.mass
          };
        } else {
          currentRegion.endDistance = point.distance;
          currentRegion.maxHeight = Math.max(currentRegion.maxHeight, height);
          currentRegion.maxFoundationDepth = Math.max(currentRegion.maxFoundationDepth, foundationDepth);
          // Track both min and max elevation
          currentRegion.minElevation = Math.min(currentRegion.minElevation, point.elevation);
          currentRegion.maxElevation = Math.max(currentRegion.maxElevation, point.elevation);
          if (!currentRegion.name && tallestBuilding.name) {
            currentRegion.name = tallestBuilding.name;
          }
          if (tallestBuilding.mass && (!currentRegion.mass || tallestBuilding.mass > currentRegion.mass)) {
            currentRegion.mass = tallestBuilding.mass;
          }
        }
      } else {
        if (currentRegion) {
          buildingRegions.push(currentRegion);
          currentRegion = null;
        }
      }
    });

    // Don't forget the last region
    if (currentRegion) {
      buildingRegions.push(currentRegion);
    }

    // Draw each building region
    buildingRegions.forEach(region => {
      const x1 = xScale(region.startDistance);
      const x2 = xScale(region.endDistance);
      const width = Math.max(x2 - x1, 15); // Minimum width for visibility

      // For HDD planning: foundations extend from lowest ground point
      // Building top is at highest ground + building height
      const baseY = yScale(region.minElevation);  // Building base at lowest ground
      const buildingY = yScale(region.maxElevation + region.maxHeight);  // Top at highest + height
      const foundationY = yScale(region.minElevation - region.maxFoundationDepth);  // Foundation below lowest

      // Foundation - extends below lowest ground point
      g.append('rect')
        .attr('x', x1)
        .attr('y', baseY)
        .attr('width', width)
        .attr('height', foundationY - baseY)
        .attr('fill', '#555')
        .attr('opacity', 0.5)
        .attr('stroke', '#333')
        .attr('stroke-width', 1);

      // Building - from lowest ground to (highest ground + height)
      g.append('rect')
        .attr('x', x1)
        .attr('y', buildingY)
        .attr('width', width)
        .attr('height', baseY - buildingY)
        .attr('fill', '#999')
        .attr('stroke', '#666')
        .attr('stroke-width', 2)
        .on('mouseover', (event: MouseEvent) => {
          this.showBuildingRegionTooltip(event, region);
        })
        .on('mouseout', () => {
          this.hideTooltip();
        });

      // Label for tall buildings
      if (region.maxHeight > 10) {
        g.append('text')
          .attr('x', x1 + width / 2)
          .attr('y', buildingY - 5)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .attr('fill', '#333')
          .text(`${region.maxHeight.toFixed(0)}m`);
      }
    });
  }

  private showBuildingRegionTooltip(event: MouseEvent, region: { name?: string; maxHeight: number; maxFoundationDepth: number; mass?: number; startDistance: number; endDistance: number }) {
    const tooltip = d3.select('#tooltip');
    const spanMeters = region.endDistance - region.startDistance;
    const u = units.distanceUnit();

    tooltip.html(`
      <strong>${region.name || 'Building'}</strong><br>
      Height: ${units.convertDistance(region.maxHeight).toFixed(1)}${u}<br>
      Foundation Depth: ${units.convertDepth(region.maxFoundationDepth).toFixed(1)}${u}<br>
      ${region.mass ? `Est. Mass: ${region.mass.toFixed(0)} tonnes<br>` : ''}
      Span: ${units.convertDistance(spanMeters).toFixed(0)}${u}
    `)
      .style('display', 'block');

    this.positionTooltip(tooltip, event);
  }
  
  private drawWaterways(
    g: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>
  ) {
    this.profile.forEach(point => {
      const waterways = point.surfaceFeatures.filter(f => f.type === 'waterway');

      waterways.forEach(waterway => {
        const x = xScale(point.distance);
        const y = yScale(point.elevation);
        const widthMeters = waterway.width || 10;
        // Convert width from meters to pixels using the x scale
        const widthPixels = xScale(widthMeters) - xScale(0);

        g.append('rect')
          .attr('x', x - widthPixels / 2)
          .attr('y', y - 3)
          .attr('width', widthPixels)
          .attr('height', 6)
          .attr('fill', '#4A90E2')
          .attr('opacity', 0.6)
          .on('mouseover', (event: MouseEvent) => {
            this.showWaterwayTooltip(event, waterway);
          })
          .on('mouseout', () => {
            this.hideTooltip();
          });

        if (waterway.name) {
          g.append('text')
            .attr('x', x)
            .attr('y', y - 10)
            .attr('text-anchor', 'middle')
            .attr('font-size', '9px')
            .attr('fill', '#2E5C8A')
            .text(waterway.name);
        }
      });
    });
  }

  private drawRoads(
    g: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>
  ) {
    // Group consecutive points with roads into road regions
    interface RoadRegion {
      startDistance: number;
      endDistance: number;
      maxWidth: number;
      minElevation: number;
      maxElevation: number;
      name?: string;
      roadClass?: string;
      maxPavementDepth: number;
      maxUtilityDepth: number;
      likelyUtilities: string[];
    }

    const roadRegions: RoadRegion[] = [];
    let currentRegion: RoadRegion | null = null;

    this.profile.forEach((point) => {
      const roads = point.surfaceFeatures.filter(f => f.type === 'road');

      if (roads.length > 0) {
        // Use the widest road at this point (likely the most significant)
        const widestRoad = roads.reduce((max, r) =>
          (r.width || 0) > (max.width || 0) ? r : max, roads[0]);

        const width = widestRoad.width || 6;
        const pavementDepth = widestRoad.pavementDepth || 0.4;
        const utilityDepth = widestRoad.utilityDepth || 1.5;

        if (!currentRegion) {
          currentRegion = {
            startDistance: point.distance,
            endDistance: point.distance,
            maxWidth: width,
            minElevation: point.elevation,
            maxElevation: point.elevation,
            name: widestRoad.name,
            roadClass: widestRoad.roadClass,
            maxPavementDepth: pavementDepth,
            maxUtilityDepth: utilityDepth,
            likelyUtilities: widestRoad.likelyUtilities || []
          };
        } else {
          currentRegion.endDistance = point.distance;
          currentRegion.maxWidth = Math.max(currentRegion.maxWidth, width);
          currentRegion.minElevation = Math.min(currentRegion.minElevation, point.elevation);
          currentRegion.maxElevation = Math.max(currentRegion.maxElevation, point.elevation);
          currentRegion.maxPavementDepth = Math.max(currentRegion.maxPavementDepth, pavementDepth);
          currentRegion.maxUtilityDepth = Math.max(currentRegion.maxUtilityDepth, utilityDepth);
          if (!currentRegion.name && widestRoad.name) {
            currentRegion.name = widestRoad.name;
          }
          if (!currentRegion.roadClass && widestRoad.roadClass) {
            currentRegion.roadClass = widestRoad.roadClass;
          }
          if (widestRoad.likelyUtilities && widestRoad.likelyUtilities.length > currentRegion.likelyUtilities.length) {
            currentRegion.likelyUtilities = widestRoad.likelyUtilities;
          }
        }
      } else {
        if (currentRegion) {
          roadRegions.push(currentRegion);
          currentRegion = null;
        }
      }
    });

    // Don't forget the last region
    if (currentRegion) {
      roadRegions.push(currentRegion);
    }

    // Draw each road region
    roadRegions.forEach(region => {
      const x1 = xScale(region.startDistance);
      const x2 = xScale(region.endDistance);
      const width = Math.max(x2 - x1, 10); // Minimum width for visibility

      // Road surface is at ground level
      const surfaceY = yScale(region.maxElevation);
      const pavementBottomY = yScale(region.maxElevation - region.maxPavementDepth);
      const utilityBottomY = yScale(region.maxElevation - region.maxUtilityDepth);

      // Draw utility zone first (deepest layer) - hatched pattern for underground services
      if (region.maxUtilityDepth > region.maxPavementDepth) {
        // Create utility corridor zone
        g.append('rect')
          .attr('x', x1)
          .attr('y', pavementBottomY)
          .attr('width', width)
          .attr('height', utilityBottomY - pavementBottomY)
          .attr('fill', '#8B4513')
          .attr('opacity', 0.3)
          .attr('stroke', '#654321')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '3,3');

        // Add utility indicator symbols
        const utilityMidY = (pavementBottomY + utilityBottomY) / 2;

        // Sewer symbol (circle)
        if (region.likelyUtilities.some(u => u.toLowerCase().includes('sewer'))) {
          g.append('circle')
            .attr('cx', x1 + width / 2 - 10)
            .attr('cy', utilityMidY)
            .attr('r', 4)
            .attr('fill', 'none')
            .attr('stroke', '#666')
            .attr('stroke-width', 1.5);
        }

        // Water main symbol (blue line)
        if (region.likelyUtilities.some(u => u.toLowerCase().includes('water'))) {
          g.append('line')
            .attr('x1', x1 + width / 2)
            .attr('y1', utilityMidY - 3)
            .attr('x2', x1 + width / 2)
            .attr('y2', utilityMidY + 3)
            .attr('stroke', '#1E90FF')
            .attr('stroke-width', 2);
        }

        // Gas symbol (yellow)
        if (region.likelyUtilities.some(u => u.toLowerCase().includes('gas'))) {
          g.append('line')
            .attr('x1', x1 + width / 2 + 10)
            .attr('y1', utilityMidY - 3)
            .attr('x2', x1 + width / 2 + 10)
            .attr('y2', utilityMidY + 3)
            .attr('stroke', '#FFD700')
            .attr('stroke-width', 2);
        }
      }

      // Draw pavement structure (sub-base, base course)
      g.append('rect')
        .attr('x', x1)
        .attr('y', surfaceY)
        .attr('width', width)
        .attr('height', pavementBottomY - surfaceY)
        .attr('fill', '#A0522D')
        .attr('opacity', 0.6)
        .attr('stroke', '#8B4513')
        .attr('stroke-width', 1);

      // Draw road surface (asphalt wearing course)
      const surfaceThickness = 3; // pixels
      g.append('rect')
        .attr('x', x1)
        .attr('y', surfaceY - surfaceThickness)
        .attr('width', width)
        .attr('height', surfaceThickness)
        .attr('fill', '#333')
        .attr('stroke', '#222')
        .attr('stroke-width', 1)
        .on('mouseover', (event: MouseEvent) => {
          this.showRoadTooltip(event, region);
        })
        .on('mouseout', () => {
          this.hideTooltip();
        });

      // Add yellow center line for major roads
      if (region.roadClass && ['motorway', 'trunk', 'primary', 'secondary'].includes(region.roadClass)) {
        g.append('line')
          .attr('x1', x1)
          .attr('y1', surfaceY - surfaceThickness / 2)
          .attr('x2', x1 + width)
          .attr('y2', surfaceY - surfaceThickness / 2)
          .attr('stroke', '#FFD700')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,2');
      }

      // Label for named roads
      if (region.name) {
        g.append('text')
          .attr('x', x1 + width / 2)
          .attr('y', surfaceY - surfaceThickness - 5)
          .attr('text-anchor', 'middle')
          .attr('font-size', '9px')
          .attr('fill', '#333')
          .text(region.name);
      }
    });
  }

  private showRoadTooltip(event: MouseEvent, region: {
    name?: string;
    maxWidth: number;
    roadClass?: string;
    startDistance: number;
    endDistance: number;
    maxPavementDepth: number;
    maxUtilityDepth: number;
    likelyUtilities: string[];
  }) {
    const tooltip = d3.select('#tooltip');
    const spanMeters = region.endDistance - region.startDistance;
    const u = units.distanceUnit();

    // Format road class for display
    const classDisplay = region.roadClass
      ? region.roadClass.charAt(0).toUpperCase() + region.roadClass.slice(1).replace('_', ' ')
      : 'Road';

    // Format utilities list
    const utilitiesDisplay = region.likelyUtilities.length > 0
      ? region.likelyUtilities.join(', ')
      : 'Unknown';

    tooltip.html(`
      <strong>${region.name || classDisplay}</strong><br>
      Class: ${classDisplay}<br>
      Width: ${units.convertDistance(region.maxWidth).toFixed(0)}${u}<br>
      Span: ${units.convertDistance(spanMeters).toFixed(0)}${u}<br>
      <br>
      <strong>Buried Infrastructure:</strong><br>
      Pavement Depth: ${units.convertDepth(region.maxPavementDepth).toFixed(1)}${u}<br>
      Max Utility Depth: ${units.convertDepth(region.maxUtilityDepth).toFixed(1)}${u}<br>
      Likely Utilities: ${utilitiesDisplay}
    `)
      .style('display', 'block');

    this.positionTooltip(tooltip, event);
  }

  private drawRailways(
    g: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>
  ) {
    // Group consecutive points with railways into railway regions
    interface RailwayRegion {
      startDistance: number;
      endDistance: number;
      maxWidth: number;
      minElevation: number;
      maxElevation: number;
      name?: string;
      railwayClass?: string;
    }

    const railwayRegions: RailwayRegion[] = [];
    let currentRegion: RailwayRegion | null = null;

    this.profile.forEach((point) => {
      const railways = point.surfaceFeatures.filter(f => f.type === 'railway');

      if (railways.length > 0) {
        // Use the widest railway at this point
        const widestRailway = railways.reduce((max, r) =>
          (r.width || 0) > (max.width || 0) ? r : max, railways[0]);

        const width = widestRailway.width || 10;

        if (!currentRegion) {
          currentRegion = {
            startDistance: point.distance,
            endDistance: point.distance,
            maxWidth: width,
            minElevation: point.elevation,
            maxElevation: point.elevation,
            name: widestRailway.name,
            railwayClass: widestRailway.railwayClass
          };
        } else {
          currentRegion.endDistance = point.distance;
          currentRegion.maxWidth = Math.max(currentRegion.maxWidth, width);
          currentRegion.minElevation = Math.min(currentRegion.minElevation, point.elevation);
          currentRegion.maxElevation = Math.max(currentRegion.maxElevation, point.elevation);
          if (!currentRegion.name && widestRailway.name) {
            currentRegion.name = widestRailway.name;
          }
          if (!currentRegion.railwayClass && widestRailway.railwayClass) {
            currentRegion.railwayClass = widestRailway.railwayClass;
          }
        }
      } else {
        if (currentRegion) {
          railwayRegions.push(currentRegion);
          currentRegion = null;
        }
      }
    });

    // Don't forget the last region
    if (currentRegion) {
      railwayRegions.push(currentRegion);
    }

    // Draw each railway region
    railwayRegions.forEach(region => {
      const x1 = xScale(region.startDistance);
      const x2 = xScale(region.endDistance);
      const width = Math.max(x2 - x1, 10); // Minimum width for visibility

      // Railway is at ground level
      const surfaceY = yScale(region.maxElevation);
      const trackThickness = 4; // Visual thickness in pixels

      // Draw railway ballast (brown/gray bed)
      g.append('rect')
        .attr('x', x1)
        .attr('y', surfaceY - trackThickness)
        .attr('width', width)
        .attr('height', trackThickness * 2)
        .attr('fill', '#8B7355')
        .attr('stroke', '#5D4E3A')
        .attr('stroke-width', 1)
        .on('mouseover', (event: MouseEvent) => {
          this.showRailwayTooltip(event, region);
        })
        .on('mouseout', () => {
          this.hideTooltip();
        });

      // Draw rails (two parallel lines)
      const railGap = 3; // pixels between rails
      g.append('line')
        .attr('x1', x1)
        .attr('y1', surfaceY - railGap / 2)
        .attr('x2', x1 + width)
        .attr('y2', surfaceY - railGap / 2)
        .attr('stroke', '#666')
        .attr('stroke-width', 2);

      g.append('line')
        .attr('x1', x1)
        .attr('y1', surfaceY + railGap / 2)
        .attr('x2', x1 + width)
        .attr('y2', surfaceY + railGap / 2)
        .attr('stroke', '#666')
        .attr('stroke-width', 2);

      // Draw sleepers/ties
      const sleeperSpacing = 8; // pixels
      for (let x = x1; x <= x1 + width; x += sleeperSpacing) {
        g.append('line')
          .attr('x1', x)
          .attr('y1', surfaceY - trackThickness + 1)
          .attr('x2', x)
          .attr('y2', surfaceY + trackThickness - 1)
          .attr('stroke', '#4A3728')
          .attr('stroke-width', 2);
      }

      // Label for named railways
      if (region.name) {
        g.append('text')
          .attr('x', x1 + width / 2)
          .attr('y', surfaceY - trackThickness - 5)
          .attr('text-anchor', 'middle')
          .attr('font-size', '9px')
          .attr('fill', '#5D4E3A')
          .text(region.name);
      }
    });
  }

  private showRailwayTooltip(event: MouseEvent, region: { name?: string; maxWidth: number; railwayClass?: string; startDistance: number; endDistance: number }) {
    const tooltip = d3.select('#tooltip');
    const spanMeters = region.endDistance - region.startDistance;
    const u = units.distanceUnit();

    // Format railway class for display
    const classDisplay = region.railwayClass
      ? region.railwayClass.charAt(0).toUpperCase() + region.railwayClass.slice(1).replace('_', ' ')
      : 'Railway';

    tooltip.html(`
      <strong>${region.name || classDisplay}</strong><br>
      Type: ${classDisplay}<br>
      Corridor Width: ${units.convertDistance(region.maxWidth).toFixed(0)}${u}<br>
      Span: ${units.convertDistance(spanMeters).toFixed(0)}${u}
    `)
      .style('display', 'block');

    this.positionTooltip(tooltip, event);
  }

  private drawHDDPath(
    g: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>
  ) {
    if (!this.hddPath || this.hddPath.length === 0) return;

    // Create line generator for the HDD path
    // Use linear interpolation to ensure visual matches calculated data exactly
    const line = d3.line<HDDPathPoint>()
      .x(d => xScale(d.distance))
      .y(d => yScale(d.elevation))
      .curve(d3.curveLinear);

    // Draw the borehole path as a thick line
    g.append('path')
      .datum(this.hddPath)
      .attr('class', 'hdd-path')
      .attr('d', line)
      .attr('stroke', '#FF4500')  // Orange-red for visibility
      .attr('stroke-width', 4)
      .attr('fill', 'none')
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round');

    // Add a thinner inner line for better visibility
    g.append('path')
      .datum(this.hddPath)
      .attr('class', 'hdd-path-inner')
      .attr('d', line)
      .attr('stroke', '#FFD700')  // Gold inner line
      .attr('stroke-width', 2)
      .attr('fill', 'none')
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round');

    // Draw entry and exit points
    const startPoint = this.hddPath[0];
    const endPoint = this.hddPath[this.hddPath.length - 1];

    // Entry point marker
    g.append('circle')
      .attr('cx', xScale(startPoint.distance))
      .attr('cy', yScale(startPoint.elevation))
      .attr('r', 8)
      .attr('fill', '#22c55e')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2);

    g.append('text')
      .attr('x', xScale(startPoint.distance))
      .attr('y', yScale(startPoint.elevation) - 15)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('font-weight', 'bold')
      .attr('fill', '#22c55e')
      .text('ENTRY');

    // Exit/End point marker
    g.append('circle')
      .attr('cx', xScale(endPoint.distance))
      .attr('cy', yScale(endPoint.elevation))
      .attr('r', 8)
      .attr('fill', '#ef4444')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2);

    g.append('text')
      .attr('x', xScale(endPoint.distance))
      .attr('y', yScale(endPoint.elevation) - 15)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('font-weight', 'bold')
      .attr('fill', '#ef4444')
      .text('EXIT');

    // Add depth markers at key points
    this.addDepthMarkers(g, xScale, yScale);

    // Add interactive hover for path details
    this.addHDDPathHover(g, xScale, yScale);
  }

  private addDepthMarkers(
    g: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>
  ) {
    if (!this.hddPath) return;

    // Find deepest point
    let deepestPoint = this.hddPath[0];
    for (const point of this.hddPath) {
      if (point.elevation < deepestPoint.elevation) {
        deepestPoint = point;
      }
    }

    // Get surface elevation at deepest point
    const surfaceElev = this.interpolateSurfaceElevation(deepestPoint.distance);
    const depth = surfaceElev - deepestPoint.elevation;

    // Draw vertical depth line
    g.append('line')
      .attr('x1', xScale(deepestPoint.distance))
      .attr('y1', yScale(surfaceElev))
      .attr('x2', xScale(deepestPoint.distance))
      .attr('y2', yScale(deepestPoint.elevation))
      .attr('stroke', '#666')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,2');

    // Depth label
    const u = units.distanceUnit();
    g.append('text')
      .attr('x', xScale(deepestPoint.distance) + 5)
      .attr('y', yScale((surfaceElev + deepestPoint.elevation) / 2))
      .attr('font-size', '10px')
      .attr('fill', '#666')
      .text(`${units.convertDepth(depth).toFixed(1)}${u} depth`);
  }

  private addHDDPathHover(
    g: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>
  ) {
    if (!this.hddPath) return;

    // Create invisible wider path for easier hovering
    const hoverLine = d3.line<HDDPathPoint>()
      .x(d => xScale(d.distance))
      .y(d => yScale(d.elevation))
      .curve(d3.curveLinear);

    g.append('path')
      .datum(this.hddPath)
      .attr('class', 'hdd-path-hover')
      .attr('d', hoverLine)
      .attr('stroke', 'transparent')
      .attr('stroke-width', 15)
      .attr('fill', 'none')
      .style('cursor', 'crosshair')
      .on('mousemove', (event: MouseEvent) => {
        const [mouseX] = d3.pointer(event);
        const distance = xScale.invert(mouseX);

        // Find closest path point
        let closest = this.hddPath![0];
        let minDist = Infinity;
        for (const point of this.hddPath!) {
          const dist = Math.abs(point.distance - distance);
          if (dist < minDist) {
            minDist = dist;
            closest = point;
          }
        }

        // Find closest profile point for coordinates and ground elevation
        let profilePoint = this.profile[0];
        let profileMinDist = Infinity;
        let profileIndex = 0;
        for (let i = 0; i < this.profile.length; i++) {
          const dist = Math.abs(this.profile[i].distance - closest.distance);
          if (dist < profileMinDist) {
            profileMinDist = dist;
            profilePoint = this.profile[i];
            profileIndex = i;
          }
        }

        const groundElev = this.interpolateSurfaceElevation(closest.distance);
        const cover = groundElev - closest.elevation;

        // Calculate azimuth from profile coordinates
        let azimuth = 0;
        if (profileIndex < this.profile.length - 1) {
          const nextPoint = this.profile[profileIndex + 1];
          azimuth = this.calculateAzimuth(
            profilePoint.latitude, profilePoint.longitude,
            nextPoint.latitude, nextPoint.longitude
          );
        } else if (profileIndex > 0) {
          const prevPoint = this.profile[profileIndex - 1];
          azimuth = this.calculateAzimuth(
            prevPoint.latitude, prevPoint.longitude,
            profilePoint.latitude, profilePoint.longitude
          );
        }

        this.showHDDTooltip(event, closest, groundElev, cover, azimuth);
      })
      .on('mouseout', () => {
        this.hideTooltip();
      });
  }

  private calculateAzimuth(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const x = Math.sin(Δλ) * Math.cos(φ2);
    const y = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);

    let bearing = Math.atan2(x, y) * 180 / Math.PI;
    return (bearing + 360) % 360;  // Normalize to 0-360
  }

  private interpolateSurfaceElevation(distance: number): number {
    if (distance <= 0) return this.profile[0].elevation;
    if (distance >= this.profile[this.profile.length - 1].distance) {
      return this.profile[this.profile.length - 1].elevation;
    }

    for (let i = 0; i < this.profile.length - 1; i++) {
      if (this.profile[i + 1].distance >= distance) {
        const t = (distance - this.profile[i].distance) /
                  (this.profile[i + 1].distance - this.profile[i].distance);
        return this.profile[i].elevation + t * (this.profile[i + 1].elevation - this.profile[i].elevation);
      }
    }

    return this.profile[this.profile.length - 1].elevation;
  }

  private showHDDTooltip(
    event: MouseEvent,
    point: HDDPathPoint,
    groundElev: number,
    cover: number,
    azimuth: number
  ) {
    const tooltip = d3.select('#tooltip');
    const u = units.distanceUnit();

    // Convert angle from horizontal to inclination from vertical
    const inclination = 90 + point.angle;

    tooltip.html(`
      <strong>HDD Borehole Survey</strong><br>
      <br>
      <strong>MD:</strong> ${units.convertDistance(point.distance).toFixed(1)} ${u}<br>
      <strong>Inc:</strong> ${inclination.toFixed(1)}°<br>
      <strong>Azi:</strong> ${azimuth.toFixed(1)}°<br>
      <br>
      <strong>Bore Elev:</strong> ${units.convertElevation(point.elevation).toFixed(1)} ${u}<br>
      <strong>Ground Elev:</strong> ${units.convertElevation(groundElev).toFixed(1)} ${u}<br>
      <strong>Cover:</strong> ${units.convertDepth(cover).toFixed(1)} ${u}<br>
      <br>
      <strong>Dogleg:</strong> ${point.curvature.toFixed(1)} ${units.doglegUnit()}
    `)
      .style('display', 'block');

    // Position tooltip within viewport
    this.positionTooltip(tooltip, event);
  }

  private positionTooltip(
    tooltip: d3.Selection<d3.BaseType, unknown, HTMLElement, any>,
    event: MouseEvent
  ) {
    const tooltipNode = tooltip.node() as HTMLElement;
    if (!tooltipNode) return;

    const tooltipRect = tooltipNode.getBoundingClientRect();
    const padding = 10;

    let left = event.pageX + padding;
    let top = event.pageY - padding;

    // Check right edge
    if (left + tooltipRect.width > window.innerWidth - padding) {
      left = event.pageX - tooltipRect.width - padding;
    }

    // Check bottom edge
    if (top + tooltipRect.height > window.innerHeight - padding) {
      top = event.pageY - tooltipRect.height - padding;
    }

    // Check left edge
    if (left < padding) {
      left = padding;
    }

    // Check top edge
    if (top < padding) {
      top = padding;
    }

    tooltip
      .style('left', left + 'px')
      .style('top', top + 'px');
  }

  private drawAxes(
    g: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>,
    width: number,
    height: number
  ) {
    const u = units.distanceUnit();
    const xAxis = d3.axisBottom(xScale)
      .tickFormat(d => `${units.convertDistance(d as number).toFixed(0)}${u}`);

    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .attr('class', 'axis')
      .call(xAxis);
    g.append('text')
      .attr('x', width / 2)
      .attr('y', height + 40)
      .attr('text-anchor', 'middle')
      .attr('font-size', '14px')
      .attr('font-weight', 'bold')
      .text(`Distance along route (${u})`);

    const yAxis = d3.axisLeft(yScale)
      .tickFormat(d => `${units.convertElevation(d as number).toFixed(0)}${u}`);

    g.append('g')
      .attr('class', 'axis')
      .call(yAxis);

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -height / 2)
      .attr('y', -60)
      .attr('text-anchor', 'middle')
      .attr('font-size', '14px')
      .attr('font-weight', 'bold')
      .text(`Elevation (${u} above sea level)`);
  }
  
  private drawLegend(
    g: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    width: number
  ) {
    const legend = g.append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${width - 200}, 20)`);

    // Base legend items
    const items: { label: string; color: string; type: 'line' | 'rect' }[] = [
      { label: 'Ground Surface', color: '#654321', type: 'line' },
      { label: 'Buildings', color: '#999', type: 'rect' },
      { label: 'Foundations', color: '#555', type: 'rect' },
      { label: 'Water Bodies', color: '#1E90FF', type: 'rect' },
      { label: 'Waterways', color: '#4A90E2', type: 'rect' },
      { label: 'Road Surface', color: '#333', type: 'rect' },
      { label: 'Road Base', color: '#A0522D', type: 'rect' },
      { label: 'Utility Zone', color: '#8B4513', type: 'rect' },
      { label: 'Railways', color: '#8B7355', type: 'rect' }
    ];

    // Add HDD path to legend if present
    if (this.hddPath && this.hddPath.length > 0) {
      items.unshift({ label: 'HDD Borehole', color: '#FF4500', type: 'line' });
    }

    // Add unique geology types found in the profile (skip Unknown)
    const geologyTypes = new Map<string, { label: string; color: string }>();
    this.profile.forEach(point => {
      if (point.geology?.superficial && point.geology.superficial.rockType !== 'Unknown') {
        const key = point.geology.superficial.code || point.geology.superficial.rockType;
        if (!geologyTypes.has(key)) {
          geologyTypes.set(key, {
            label: point.geology.superficial.rockType,
            color: point.geology.superficial.color || '#D4B896'
          });
        }
      }
      if (point.geology?.bedrock && point.geology.bedrock.rockType !== 'Unknown') {
        const key = point.geology.bedrock.code || point.geology.bedrock.rockType;
        if (!geologyTypes.has(key)) {
          geologyTypes.set(key, {
            label: point.geology.bedrock.rockType,
            color: point.geology.bedrock.color || '#CCCCCC'
          });
        }
      }
      // Add SSURGO soil types (US)
      if (point.geology?.soil && point.geology.soil.rockType !== 'Unknown') {
        const key = `soil:${point.geology.soil.code || point.geology.soil.rockType}`;
        if (!geologyTypes.has(key)) {
          geologyTypes.set(key, {
            label: `Soil: ${point.geology.soil.rockType}`,
            color: point.geology.soil.color || '#8B7355'
          });
        }
      }
    });

    // Add geology to legend (limit to 6 to avoid overflow)
    let geologyCount = 0;
    geologyTypes.forEach((geo) => {
      if (geologyCount < 6 && geo.label && geo.label !== 'Unknown') {
        items.push({ label: geo.label, color: geo.color, type: 'rect' });
        geologyCount++;
      }
    });
    
    items.forEach((item, i) => {
      const y = i * 25;
      
      if (item.type === 'line') {
        legend.append('line')
          .attr('x1', 0)
          .attr('y1', y + 5)
          .attr('x2', 20)
          .attr('y2', y + 5)
          .attr('stroke', item.color)
          .attr('stroke-width', 3);
      } else {
        legend.append('rect')
          .attr('x', 0)
          .attr('y', y)
          .attr('width', 20)
          .attr('height', 10)
          .attr('fill', item.color);
      }
      
      legend.append('text')
        .attr('x', 30)
        .attr('y', y + 9)
        .attr('font-size', '12px')
        .text(item.label);
    });
  }
  
  private showWaterwayTooltip(event: MouseEvent, waterway: SurfaceFeature) {
    const tooltip = d3.select('#tooltip');

    tooltip.html(`
      <strong>${waterway.name || 'Waterway'}</strong><br>
      Width: ${waterway.width?.toFixed(1)}m
    `)
      .style('display', 'block');

    this.positionTooltip(tooltip, event);
  }
  
  private hideTooltip() {
    d3.select('#tooltip').style('display', 'none');
  }
}
